import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../lib/api";
import { parseMoney } from "../../../lib/auditMath";

const makeRecord = (saved, profit = false) => ({
  amount: String((profit ? saved.annual_profit_inr : saved.amount) ?? ""),
  choice: saved.choice ?? null, revision: saved.revision, saved,
  amountDirty: false, choiceDirty: false, av: 0, cv: 0,
  busy: false, status: "", conflict: null, request: null,
});

export function useAuditSave(onConfig) {
  const records = useRef({});
  const timers = useRef({});
  const mounted = useRef(true);
  const [view, setView] = useState({});
  const notify = useCallback(() => { if (mounted.current) setView({ ...records.current }); }, []);
  const saveRef = useRef(null);

  const initialize = useCallback((data) => {
    Object.values(timers.current).forEach(clearTimeout);
    records.current = Object.fromEntries(Object.entries(data.rows).map(([id, saved]) => [id, makeRecord(saved)]));
    records.current["annual-profit"] = makeRecord(data.profile, true);
    notify();
  }, [notify]);

  const schedule = useCallback((id, delay = 800) => {
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => saveRef.current(id), delay);
  }, []);

  const save = useCallback(async (id) => {
    clearTimeout(timers.current[id]);
    const r = records.current[id];
    if (!r || r.busy || r.conflict) return;
    const parsed = parseMoney(r.amount);
    const amountPending = r.amountDirty && !parsed.error;
    const choicePending = r.choiceDirty;
    if (!amountPending && !choicePending) return;
    const profit = id === "annual-profit";
    const snapshot = { av: r.av, cv: r.cv, amountPending, choicePending };
    const fields = { ...(amountPending ? { [profit ? "annual_profit_inr" : "amount"]: parsed.value } : {}),
      ...(choicePending ? { choice: r.choice } : {}) };
    const signature = JSON.stringify({ ...fields, revision: r.revision });
    // Retrying an ambiguous network failure reuses its idempotency key.
    if (r.request?.signature !== signature) r.request = { signature, id: crypto.randomUUID() };
    const body = { ...fields, revision: r.revision, request_id: r.request.id };
    r.busy = true; r.status = "saving"; notify();
    try {
      const { data } = await api.patch(profit ? "/gaps-audit/profile/annual-profit" : `/gaps-audit/fixed/${id}`, body, { timeout: 20000 });
      const saved = profit ? data.profile : data.row;
      r.revision = saved.revision; r.saved = saved; r.request = null;
      if (snapshot.amountPending && r.av === snapshot.av) r.amountDirty = false;
      if (snapshot.choicePending && r.cv === snapshot.cv) r.choiceDirty = false;
      r.status = "saved";
      if (mounted.current) onConfig(data.config);
    } catch (err) {
      r.status = "failed";
      if (err.response?.status === 409) r.conflict = err.response.data.detail.current;
    } finally {
      r.busy = false;
      if (r.status !== "failed" && ((r.amountDirty && !parseMoney(r.amount).error) || r.choiceDirty)) {
        r.status = "saving";
        schedule(id, 0);
      }
      notify();
    }
  }, [notify, onConfig, schedule]);
  saveRef.current = save;

  const change = (id, field, value) => {
    const r = records.current[id];
    r[field] = value;
    if (field === "amount") { r.amountDirty = true; r.av += 1; }
    else { r.choiceDirty = true; r.cv += 1; }
    if (!r.conflict) r.status = field === "amount" && parseMoney(value).error ? "" : "saving";
    clearTimeout(timers.current[id]);
    if (!r.conflict) schedule(id, field === "choice" ? 0 : 800);
    notify();
  };

  const retry = (id) => {
    const r = records.current[id];
    if (r.conflict) {
      if (!window.confirm("This value changed in another window. Replace the newer saved value with your current edit?")) return;
      r.revision = r.conflict.revision; r.saved = r.conflict; r.conflict = null; r.request = null;
    }
    save(id);
  };

  const acceptSaved = (id) => {
    const r = records.current[id];
    records.current[id] = makeRecord(r.conflict || r.saved, id === "annual-profit");
    records.current[id].status = "saved";
    notify();
  };

  useEffect(() => {
    mounted.current = true;
    const activeTimers = timers.current;
    return () => { mounted.current = false; Object.values(activeTimers).forEach(clearTimeout); };
  }, []);

  const dirty = Object.values(view).some((r) => r.busy || r.amountDirty || r.choiceDirty || r.status === "failed");
  return { records: view, initialize, change, save, retry, acceptSaved, dirty };
}